import { useEffect } from "react";
import { registerShortcut } from "@/lib/shortcut-registry";

/**
 * Shared bail-out predicate for global single-key shortcut listeners.
 * Returns `true` when the event should be IGNORED (i.e. the shortcut must
 * NOT fire). Centralised so `useGlobalHotkey` and ad-hoc keydown listeners
 * (e.g. the dynamic Shift+letter member filter shortcuts in project-tree)
 * share one source of truth for "is the user typing / is an overlay open".
 *
 * Bails when:
 *   - `event.defaultPrevented` is true (downstream already handled it).
 *   - The event target is an `<input>`, `<textarea>`, `<select>`, or any
 *     element with `contentEditable === 'true'` (covers Tiptap composer),
 *     OR a descendant of such an element.
 *   - The DOM contains an open Radix overlay — open dialog, focused cmdk
 *     input, or open listbox — so shortcuts cannot fire while the user
 *     is typing into a Command palette / combobox.
 */
export function shouldIgnoreKeydown(event: KeyboardEvent): boolean {
  if (event.defaultPrevented) return true;

  const target = event.target as Element | null;
  if (target && typeof (target as HTMLElement).closest === "function") {
    const tag = target.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return true;
    if ((target as HTMLElement).isContentEditable) return true;
    if (target.closest('[contenteditable="true"]')) return true;
  }

  if (typeof document !== "undefined") {
    const overlay = document.querySelector(
      '[role="dialog"][data-state="open"], [cmdk-root] [cmdk-input]:focus, [role="listbox"][data-state="open"]',
    );
    if (overlay) return true;
  }

  return false;
}

export interface UseGlobalHotkeyOptions {
  /**
   * If true, the hotkey will fire even when a modifier key
   * (Ctrl/Meta/Alt/Shift) is held. Defaults to false so that modifier
   * combinations (e.g. Cmd+T to open a new browser tab, Shift+T for a
   * future capital-letter binding) are left alone.
   */
  allowInModifier?: boolean;
  /**
   * Conditionally disable the listener without unmounting the hook.
   * Defaults to true.
   */
  enabled?: boolean;
  /**
   * Optional metadata used to auto-register the shortcut in the global
   * shortcut registry, which powers the '?' help overlay. When omitted
   * the shortcut still works but won't appear in the help sheet.
   */
  description?: string;
  /** Group label used to bucket the shortcut in the help sheet. */
  group?: string;
}

/**
 * Registers a single, safety-checked `keydown` listener for a single-key
 * shortcut. Bails out (does NOT invoke `handler`) when any of the
 * following are true:
 *
 *   1. The event target is an `<input>`, `<textarea>`, `<select>`, or any
 *      element with `contentEditable === 'true'` (covers Tiptap composer).
 *   2. The event target's closest ancestor matches
 *      `[contenteditable="true"]` (covers nested Tiptap nodes).
 *   3. Any modifier key is pressed (Ctrl / Meta / Alt / Shift) — unless
 *      the caller explicitly opts in via `options.allowInModifier`.
 *   4. `event.defaultPrevented` is true (something downstream handled it
 *      already, e.g. Radix Select type-ahead, popover keyboard nav).
 *   5. The DOM contains an open Radix overlay — open dialog, focused
 *      cmdk input, or open listbox — so single-letter shortcuts cannot
 *      fire while the user is typing into a Command palette / combobox.
 *   6. The pressed key does not match `key` (case-insensitive, compares
 *      `event.key` so QWERTZ vs QWERTY both work).
 *
 * If a new edge case is discovered (e.g. a future rich-text component
 * that doesn't set `contenteditable`), fix it here — never re-derive
 * the safety check per call site.
 */
export function useGlobalHotkey(
  key: string,
  handler: (event: KeyboardEvent) => void,
  options: UseGlobalHotkeyOptions = {},
): void {
  const { allowInModifier = false, enabled = true, description, group } = options;

  useEffect(() => {
    if (!enabled) return;
    if (!description) return;
    return registerShortcut({
      id: `${group ?? "Allgemein"}::${key}::${description}`,
      key,
      description,
      group: group ?? "Allgemein",
    });
  }, [enabled, key, description, group]);

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;

    const targetKey = key.toLowerCase();

    const onKeyDown = (event: KeyboardEvent) => {
      // (6) key match — case-insensitive, uses `event.key` not `event.code`
      if (!event.key || event.key.toLowerCase() !== targetKey) return;

      // (3) modifier guard
      if (
        !allowInModifier &&
        (event.ctrlKey || event.metaKey || event.altKey || event.shiftKey)
      ) {
        return;
      }

      // (1) (2) (4) (5) shared bail-out matrix — typing target, open overlay,
      // defaultPrevented. See shouldIgnoreKeydown for the full list.
      if (shouldIgnoreKeydown(event)) return;

      handler(event);
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [key, handler, allowInModifier, enabled]);
}
